import React, { useImperativeHandle } from 'react';

import { Button, Tooltip, Toolbar, ToolbarGroup, ToolbarDivider, makeStyles, Label } from '@fluentui/react-components';
import { ChevronDoubleLeft16Filled, ChevronLeft16Filled, ChevronRight16Filled, ChevronDoubleRight16Filled } from '@fluentui/react-icons';

import { AgGridReact } from 'ag-grid-react';

const useStyles = makeStyles({
    root: {
        marginTop: '4px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    toolbarGroup: {
        paddingTop: '2px',
    }
});

export interface GridPaginationProps {
    gridRef: React.RefObject<AgGridReact<any>>;
}

export type GridPaginationRef = {
    invoke: () => void;
};

export const GridPagination = React.forwardRef<GridPaginationRef, GridPaginationProps>(({ gridRef }, ref) => {
    const styles = useStyles();

    const [gridData, setGridData] = React.useState({
        pageSize: 0,
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
    });

    useImperativeHandle(ref, () => ({
        invoke() {
            const api = gridRef.current?.api;
            if (api) {
                setGridData({
                    pageSize: api.paginationGetPageSize(),
                    totalPages: api.paginationGetTotalPages(),
                    currentPage: api.paginationGetCurrentPage() + 1,
                    totalItems: api.paginationGetRowCount(),
                });
            }
        }
    }));

    const onFirstPage = React.useCallback(() => {
        gridRef.current?.api.paginationGoToFirstPage();
    }, [gridRef]);

    const onPreviousPage = React.useCallback(() => {
        gridRef.current?.api.paginationGoToPreviousPage();
    }, [gridRef]);

    const onNextPage = React.useCallback(() => {
        gridRef.current?.api.paginationGoToNextPage();
    }, [gridRef]);

    const onLastPage = React.useCallback(() => {
        gridRef.current?.api.paginationGoToLastPage();
    }, [gridRef]);

    return (
        <div className={styles.root}>
            Total items: {gridData.totalItems}
            <Toolbar aria-label='pagination'>

                <ToolbarGroup role='prev' className={styles.toolbarGroup}>
                    <Tooltip content="First" relationship="label" withArrow>
                        <Button
                            icon={<ChevronDoubleLeft16Filled />}
                            shape='circular'
                            appearance="subtle"
                            onClick={onFirstPage}
                        />
                    </Tooltip>

                    <Tooltip content="Previous" relationship="label" withArrow>
                        <Button
                            shape='circular'
                            icon={<ChevronLeft16Filled />}
                            appearance="subtle"
                            onClick={onPreviousPage}
                        />
                    </Tooltip>
                </ToolbarGroup>

                <ToolbarDivider />
                
                <Label>
                    Page {gridData.currentPage} of {gridData.totalPages}
                </Label>

                <ToolbarDivider />

                <ToolbarGroup role='next' className={styles.toolbarGroup}>
                    <Tooltip content="Next" relationship="label" withArrow>
                        <Button
                            shape='circular'
                            icon={<ChevronRight16Filled />}
                            appearance="subtle"
                            onClick={onNextPage}
                        />
                    </Tooltip>

                    <Tooltip content="Last" relationship="label" withArrow>
                        <Button
                            shape='circular'
                            icon={<ChevronDoubleRight16Filled />}
                            appearance="subtle"
                            onClick={onLastPage}
                        />
                    </Tooltip>
                </ToolbarGroup>

            </Toolbar>
        </div >
    );
});